import { DateTime } from 'luxon';
function isNumeric(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}
function isEmptyForRequired(value) {
    return (typeof value === 'undefined' ||
        (typeof value === 'string' && value.trim().length === 0) ||
        value === null ||
        (typeof value === 'number' && (isNaN(value) || !isFinite(value))) ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0));
}
const validationRulesHandlers = {
    required(value, key, { errorText = undefined }) {
        if (isEmptyForRequired(value)) {
            return typeof errorText === 'string' ? errorText : `'${key}' is missing.`;
        }
        else {
            return true;
        }
    },
    atLeastOneRequired(value, key, { errorText = undefined }) {
        if (typeof value === 'object' && Object.keys(value).length > 1) {
            const entries = Object.entries(value);
            const emptyValues = entries.filter((entry) => {
                return isEmptyForRequired(entry[1]);
            });
            if (emptyValues.length === entries.length) {
                return typeof errorText === 'string'
                    ? errorText
                    : `At least one of the following fields must be filled: ${Object.keys(value).join(', ')}`;
            }
            else {
                return true;
            }
        }
        else {
            throw new Error('Value must be object with multiple keys');
        }
    },
    number(value, key, { errorText = undefined }) {
        if (value !== undefined && isNumeric(value)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be number.`;
        }
    },
    nonNegativeNumber(value, key, { errorText = undefined }) {
        if (value !== undefined && isNumeric(value) && value >= 0) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be non-negative number.`;
        }
    },
    positiveNumber(value, key, { errorText = undefined }) {
        if (value !== undefined && isNumeric(value) && value > 0) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be positive number.`;
        }
    },
    integer(value, key, { errorText = undefined }) {
        if (value !== undefined && Number.isInteger(value)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be integer.`;
        }
    },
    nonNegativeInteger(value, key, { errorText = undefined }) {
        if (Number.isInteger(value) && value >= 0) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be non-negative integer.`;
        }
    },
    positiveInteger(value, key, { errorText = undefined }) {
        if (Number.isInteger(value) && value > 0) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be positive integer.`;
        }
    },
    min(value, key, { minValue, errorText = undefined }) {
        if (value >= minValue) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be equal or greater to ${minValue}.`;
        }
    },
    integers(values, key, { errorText = undefined }) {
        if (Array.isArray(values)) {
            const allInt = values.every((el) => Number.isInteger(el));
            if (allInt) {
                return true;
            }
        }
        return typeof errorText === 'string' ? errorText : `'${key}' must be array of integers.`;
    },
    string(value, key, { errorText = undefined }) {
        if (typeof value === 'string') {
            return true;
        }
        return typeof errorText === 'string' ? errorText : `'${key}' must be a text.`;
    },
    object(value, key, { errorText = undefined }) {
        if (typeof value === 'object' && !Array.isArray(value)) {
            return true;
        }
        return typeof errorText === 'string' ? errorText : `'${key}' must be a object.`;
    },
    boolean(value, key, { errorText = undefined }) {
        if (typeof value === 'boolean') {
            return true;
        }
        return typeof errorText === 'string' ? errorText : `'${key}' must be a boolean.`;
    },
    array(value, key, { errorText = undefined }) {
        if (typeof value === 'object' && Array.isArray(value)) {
            return true;
        }
        return typeof errorText === 'string' ? errorText : `'${key}' must be a array.`;
    },
    url(value, key, { errorText = undefined }) {
        const urlRegex = '^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
        const regex = new RegExp(urlRegex, '');
        if (typeof value === 'string' && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid url.`;
        }
    },
    email(value, key, { errorText = undefined }) {
        const expression = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const regex = new RegExp(expression);
        if (typeof value === 'string' && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid email.`;
        }
    },
    uuid(value, key, { errorText = undefined }) {
        const expression = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        const regex = new RegExp(expression);
        if (typeof value === 'string' && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid UUID.`;
        }
    },
    phone(value, key, { errorText = undefined }) {
        const expression = /^(((\+|00)\d{1,3})|(\((\+|00)\d{1,3})\))?[\s-]?(\d{2,}[\s-]?){3,}\d{2}$/;
        const regex = new RegExp(expression);
        if (typeof value === 'string' && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid telephone number.`;
        }
    },
    employeeDiscountNumber(value, key, { errorText = undefined }) {
        const expression = /^(2931)\d{9}$/;
        const regex = new RegExp(expression);
        if (typeof value === 'string' && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid employee discount number.`;
        }
    },
    ean(value, key, { errorText = undefined }) {
        function checksum(number) {
            const res = number
                .substr(0, 12)
                .split('')
                .map(function (n) {
                return +n;
            })
                .reduce(function (sum, a, idx) {
                return idx % 2 ? sum + a * 3 : sum + a;
            }, 0);
            return (10 - (res % 10)) % 10;
        }
        const expression = /[0-9]{13}/;
        const regex = new RegExp(expression);
        if (typeof value === 'string' && value.length === 13 && +value[12] === checksum(value) && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid ean.`;
        }
    },
    dateFilter(value, key, { errorText = undefined }) {
        function hasDuplicates(value) {
            const values = value.split(',');
            return new Set(values).size === values.length;
        }
        const expression = /^(([0-9]{4}(,[0-9]{4})*)|(\*))-(((0[1-9]|1[0-2])(,(0[1-9]|1[0-2]))*)|(\*))-(((0[1-9]|1[0-9]|2[0-9]|3[0-1])(,(0[1-9]|1[0-9]|2[0-9]|3[0-1]))*)|(\*))$/;
        const regex = new RegExp(expression);
        const duplicates = value.split('-').filter((current) => {
            return !hasDuplicates(current);
        });
        if (typeof value === 'string' && value.match(regex) && duplicates.length === 0) {
            return true;
        }
        return typeof errorText === 'string'
            ? errorText
            : `'${key}' ${value} must be in yyyy-mm-dd format with no duplicate values`;
    },
    dateFilterGeneric(values, key, { errorText = undefined }) {
        if (!Array.isArray(values)) {
            values = [values];
        }
        return validationRulesHandlers.dateFilters(values, key, { errorText });
    },
    dateFilters(values, key, { errorText = undefined }) {
        if (Array.isArray(values)) {
            const results = values.map((el) => validationRulesHandlers.dateFilter(el, key, { errorText }));
            if (results.every((result) => result === true)) {
                return true;
            }
            else {
                return results.find((res) => res !== true);
            }
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be array`;
        }
    },
    weekFilter(value, key, { errorText = undefined }) {
        const expression = /^[1-7](,[1-7])*$/;
        const regex = new RegExp(expression);
        const values = value.split(',');
        if (typeof value === 'string' && value.match(regex) && new Set(values).size === values.length) {
            return true;
        }
        return typeof errorText === 'string'
            ? errorText
            : `'${key}' must be one or more values between 1 and 7 and can't have duplicates.`;
    },
    dateAndWeekFilterEmpty(value, key, { errorText = undefined }) {
        if (!isEmpty(value.weekDay) || !isEmpty(value.date)) {
            return true;
        }
        return typeof errorText === 'string'
            ? errorText
            : `'${key}' there must be at least one of weekday/date filters populated`;
    },
    hours(value, key, { errorText = undefined }) {
        if (convertTime(value).isValid) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' time must be in hh:mm:ss format.`;
        }
    },
    startBeforeEnd(value, key, { errorText = undefined }) {
        if (isEmpty(value.from) || isEmpty(value.to))
            return true;
        const from = convertTime(value.from);
        const to = convertTime(value.to);
        if (from.isValid && to.isValid && to < from) {
            return typeof errorText === 'string' ? errorText : `'${key}' pickupFrom must be before pickupTo.`;
        }
        else {
            return true;
        }
    },
    uuids(value, key, { errorText = undefined }) {
        if (arrayHandler(value, key, validationRulesHandlers['uuid'])) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' all UUIDS must be valid.`;
        }
    },
    uuidsCustom(value, key, { errorText = undefined }) {
        if (arrayHandler(value, key, validationRulesHandlers['uuidCustom'])) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' all UUIDS must be valid.`;
        }
    },
    uuidCustom(value, key, { errorText = undefined }) {
        const expression = /^[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}$/;
        const regex = new RegExp(expression);
        if (typeof value === 'string' && value.match(regex)) {
            return true;
        }
        else {
            return typeof errorText === 'string' ? errorText : `'${key}' must be valid UUID.`;
        }
    },
};
function isEmpty(value) {
    return (typeof value === 'string' && value.length === 0) || typeof value === 'undefined' || value === null;
}
function skipCheck(rule, value) {
    return ((rule !== 'required' && isEmpty(value)) ||
        (rule !== 'custom' && typeof validationRulesHandlers[rule] !== 'function'));
}
function convertTime(value) {
    const timeFormat = 'HH:mm:ss';
    return DateTime.fromFormat(value, timeFormat);
}
function arrayHandler(value, key, arrayElementResolver) {
    return (Array.isArray(value) &&
        value.map((current) => arrayElementResolver(current, key, {})).filter((current) => current !== true).length === 0);
}
const Validator = {
    arrayHandler,
    validateInputs(data, validationRules) {
        const validationFields = Object.keys(validationRules);
        return validationFields.reduce((carry, key) => {
            const rules = validationRules[key];
            const value = data[key];
            const errors = rules.reduce((carry, rule) => {
                if (typeof rule === 'string') {
                    if (!skipCheck(rule, value)) {
                        const error = validationRulesHandlers[rule](value, key, {});
                        if (typeof error === 'string') {
                            return [...carry, error];
                        }
                    }
                }
                else if (typeof rule === 'object') {
                    const condition = typeof rule.condition === 'function' ? rule.condition : () => true;
                    const doSkipCheck = (name, value) => {
                        if (name !== 'custom') {
                            return !skipCheck(name, value);
                        }
                        return true;
                    };
                    if (condition(data) === true && doSkipCheck(rule.name, value)) {
                        const validationHandler = (() => {
                            if (rule.name === 'custom') {
                                if (typeof rule.customValidator !== 'function') {
                                    throw new Error('Custom validation rule must implement customValidator handler');
                                }
                                return rule.customValidator;
                            }
                            else {
                                return validationRulesHandlers[rule.name];
                            }
                        })();
                        const error = validationHandler(value, key, rule.options || {});
                        if (typeof error === 'string') {
                            return [...carry, error];
                        }
                    }
                }
                return carry;
            }, []);
            if (errors.length) {
                carry[key] = errors;
            }
            return carry;
        }, {});
    },
};
export default Object.freeze(Validator);
